import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useValidationStore } from "@/state/validationState";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";

export type ProjectFlowSettings =
  components["schemas"]["ProjectFlowSettings"];
export type ReplaceProjectFlowSettingsRequest =
  components["schemas"]["ReplaceProjectFlowSettingsRequest"];
export type FlowSettingView = components["schemas"]["FlowSettingView"];
export type FlowSettingWrite = components["schemas"]["FlowSettingWrite"];
export type FlowSetting = components["schemas"]["FlowSetting"];
export type FlowSettingState = components["schemas"]["FlowSettingState"];
export type FlowSelection = components["schemas"]["FlowSelection"];
export type FlowSlot = components["schemas"]["FlowSlot"];
export type FlowSlotKind = components["schemas"]["FlowSlotKind"];
export type FlowLanguages = components["schemas"]["FlowLanguages"];
export type FlowFinding = components["schemas"]["FlowFinding"];
export type FlowPathwaySelection =
  components["schemas"]["FlowPathwaySelection"];
export type FlowContract = components["schemas"]["FlowContract"];
export type FlowRegionOwner = components["schemas"]["FlowRegionOwner"];

/**
 * The Project's typed flow settings (#854's contract, consumed by #855).
 *
 * `enabled` exists because the only caller that needs this read is the section
 * surface, which serves six slugs from one component and may not call a hook
 * conditionally. A disabled query fetches nothing and stays `pending`, which is
 * the state the Pathways/Rules controls already render as "not read yet" — so a
 * section that has no flow control never issues the request.
 */
export function useFlowSettingsQuery(
  projectId: string,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<ProjectFlowSettings> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.projectFlow(projectId),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/flow", {
          params: { path: { projectId } },
          signal,
        }),
      );
    },
    enabled: enabled && projectId.length > 0,
  });
}

/**
 * One revisioned typed flow write.
 *
 * The request carries `expectedDraftVersion` exactly as the caller read it —
 * this hook never re-reads a token at submit time, which is the whole point of
 * the revision check: a write built on a revision that moved must be REFUSED so
 * the author sees what changed, not silently rebased onto a document they never
 * saw.
 */
export function useReplaceFlowSettingsMutation(
  projectId: string,
): UseMutationResult<
  ProjectFlowSettings,
  unknown,
  ReplaceProjectFlowSettingsRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const clearValidation = useValidationStore((state) => state.clear);

  return useMutation({
    async mutationFn(body) {
      return unwrap(
        await client.PUT("/v1/projects/{projectId}/flow", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    async onSuccess(saved) {
      // The RESPONSE is the effective-state readback the ticket asks for: the
      // server returns the same resource the GET serves, derived from the
      // document this write left. Seeding it is what makes the surface show
      // server-defined effective state after a save rather than the values the
      // author typed.
      queryClient.setQueryData(queryKeys.projectFlow(projectId), saved);
      await settleFlowWrite(queryClient, projectId, clearValidation);
    },
    onError(error) {
      const failure = classifyFlowWriteFailure(error);
      // `responseFiltering`'s rule, for the same two reasons: a stale refusal
      // PROVES the draft moved, and a transport/5xx failure is ambiguous — the
      // server may have committed before the reply was lost. Refresh in both
      // cases, and never resend automatically.
      if (failure.kind !== "stale" && failure.kind !== "ambiguous") {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectFlow(projectId),
      });
      void settleFlowWrite(queryClient, projectId, clearValidation);
    },
    retry: false,
  });
}

/**
 * What every mounted reader has to re-observe after the flow document moved.
 *
 * Wider than `responseFiltering`'s settlement by exactly the pin set and the
 * section projections, and for a reason the contract states: an accepted flow
 * write REFRESHES the Always/automatic selection persisted with each adopted
 * Pathway pin, in the same commit. `GET /adoptions` therefore answers
 * differently after this write, and every surface derived from it — the section
 * summaries, the Pathway rows, the Clinical summary's pin cards — is describing
 * a selection the service has already replaced.
 *
 * The draft version moved too, so the validation report certifies content it
 * never saw and a frozen candidate's server-derived freshness no longer
 * describes the Project. Both are dropped for the reason the adoption writer
 * drops them.
 */
async function settleFlowWrite(
  queryClient: QueryClient,
  projectId: string,
  clearValidation: (projectId: string) => void,
): Promise<void> {
  clearValidation(projectId);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.draft(projectId) }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.projectAdoptions(projectId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.projectSections(projectId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.projectOverview(projectId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.previewCandidates(projectId),
    }),
  ]);
}

export type FlowWriteFailure =
  /** The draft moved since it was read. Carries the server's current one. */
  | { kind: "stale"; currentDraftVersion: string | null }
  /** The pinned base cannot be verified, or the draft is not composable. */
  | { kind: "conflict"; message: string }
  /** No usable clinical engine, so no loader authority to validate against. */
  | { kind: "upstream" }
  | { kind: "denied" }
  /**
   * The typed validator's own sentence.
   *
   * NO FIELD POINTER. `ErrorDetail.field` is documented as "a path, never
   * content", and #488's amendment removes a dotted address from every Project
   * route — `projectPayloadBoundary` bans reading the envelope's pointer for
   * exactly the reason a local rename would defeat a narrower guard. The
   * refusal's message is already field-addressed in words.
   */
  | { kind: "invalid"; message: string; requestId: string | undefined }
  | { kind: "ambiguous"; requestId: string | undefined }
  | { kind: "error"; code: string; requestId: string | undefined };

function envelopeString(error: ApiError, key: string): string | null {
  const value = error.envelope[key];
  return typeof value === "string" ? value : null;
}

export function classifyFlowWriteFailure(error: unknown): FlowWriteFailure {
  if (!(error instanceof ApiError)) {
    return { kind: "ambiguous", requestId: undefined };
  }
  if (error.code === "stale_draft_version") {
    return {
      kind: "stale",
      currentDraftVersion: envelopeString(error, "currentDraftVersion"),
    };
  }
  if (error.isForbidden || error.isUnauthenticated || error.status === 404) {
    return { kind: "denied" };
  }
  if (error.status === 409) {
    // `projection_identity_mismatch` and the plain conflict, together: both say
    // the Project's own base or stored draft is the problem, neither is fixed
    // by resending, and the server's sentence is the only one that knows which.
    return { kind: "conflict", message: error.message };
  }
  if (error.status === 502 || error.status === 503 || error.status === 504) {
    return { kind: "upstream" };
  }
  if (error.status >= 500 && !error.isNotImplemented) {
    return { kind: "ambiguous", requestId: error.requestId };
  }
  if (error.status === 400 || error.status === 413 || error.status === 422) {
    // The SERVER's own sentence, deliberately: a 422 here is the typed
    // validator reproducing one of the pinned loader's refusals, and
    // paraphrasing it would either lose which rule was broken or duplicate the
    // loader's rules in the browser.
    return {
      kind: "invalid",
      message: error.message,
      requestId: error.requestId,
    };
  }
  return { kind: "error", code: error.code, requestId: error.requestId };
}
